import * as React from 'react'
import Head from 'next/head'
import { NextSeo } from 'next-seo'
import {
	Checkbox,
	Grid,
	Paper,
	Text,
	TextInput,
	Title,
	Badge,
} from '@mantine/core'
import { useRouter } from 'next/router'
import { ACCENT_COLOUR } from '../_app'

// @ts-ignore
import iso3311a2 from 'iso-country-lookup'
import pluralize from 'pluralize'

import * as Types from 'declarations'
// import * as DataUtil from 'lib/data'
import {
	countryIsoCodeToSlugName,
	countryNameSlugToIsoCode,
	getDefaultImage,
} from 'lib/helper'
import * as RedisUtil from 'lib/redis'

// import { useIsMobile } from '../../lib/useIsMobile'

import { withVersion } from 'lib/withVersion'
import PageTemplate from 'components/PageTemplate'
import CountryFilterInput from 'components/CountryFilterInput'
import TripFilterInput from 'components/TripFilterInput'
import GuideDirectoryList from 'components/GuideDirectoryList'

interface GuidesPageProps {
	guideOverviews: Types.API.GuideCompanyOverview[]
	distinctCountries: string[]
	version: Types.VERSION
}

interface Fitler {
	agencyName: string
	countries: string[]
	peaks: number[]
	certifiedOnly: boolean
	premiumOnly: boolean
}

// Utility function to capitalize the first letter and lowercase the rest
const capitalizeFirstLetter = (str: string) =>
	!str ? '' : str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

const GuidesPage = ({
	guideOverviews,
	distinctCountries,
	version,
}: GuidesPageProps) => {
	const router = useRouter()
	// const isMobile = useIsMobile()

	// type Query = {
	// 	agency?: string
	// 	countries?: string[]
	// 	peaks?: number[]
	// }
	type QueryParams = {
		agency?: string
		countries?: string[]
		trip?: number[]
		certifiedOnly?: boolean
		premiumOnly?: boolean
	}

	const [filter, setFilter] = React.useState<Fitler>({
		agencyName: '',
		countries: [],
		peaks: [],
		certifiedOnly: false,
		premiumOnly: false,
	})

	// Filters only get pushed to the URL once the user has actually interacted
	// with them — otherwise the sync on mount would clobber deep-linked state
	// (e.g. the ?page= param of crawlable guide directory pagination).
	const userTouchedFiltersRef = React.useRef(false)

	const setUserTouchedFilter = React.useCallback(
		(updater: Fitler | ((prev: Fitler) => Fitler)) => {
			userTouchedFiltersRef.current = true
			setFilter(updater)
		},
		[],
	)

	// Update URL parameters based on filter state
	React.useEffect(() => {
		if (!userTouchedFiltersRef.current) return
		const query: QueryParams = {}
		if (filter.agencyName) query.agency = filter.agencyName
		if (filter.countries.length > 0)
			query.countries = filter.countries.map((iso) =>
				countryIsoCodeToSlugName(iso),
			)
		if (filter.peaks.length > 0) query.trip = filter.peaks

		if (filter.certifiedOnly) {
			query.certifiedOnly = filter.certifiedOnly
		}

		if (filter.premiumOnly) {
			query.premiumOnly = filter.premiumOnly
		}

		router.push({ query }, undefined, { shallow: true })
	}, [filter]) // Update URL whenever the filter changes

	// Initialize filter state from URL parameters
	React.useEffect(() => {
		const { countries, agency, trip, certifiedOnly, premiumOnly } = router.query

		const newFilter: Fitler = {
			agencyName: '',
			countries: [],
			peaks: [],
			certifiedOnly: false,
			premiumOnly: false,
		}

		if (countries) {
			const countriesToParse = Array.isArray(countries)
				? countries
				: [countries]
			const isoCountries = countriesToParse
				.map((country) => {
					const formattedCountry = capitalizeFirstLetter(country)
					return countryNameSlugToIsoCode(formattedCountry)
				})
				.filter((iso) => iso !== 'country-not-found')
				.filter((iso) => !!iso)
				.map((iso) => iso.toUpperCase())

			newFilter.countries = isoCountries
		}

		if (agency) {
			newFilter.agencyName = agency as string
		}

		if (typeof certifiedOnly !== undefined) {
			newFilter.certifiedOnly = Boolean(certifiedOnly)
		}

		if (typeof premiumOnly !== undefined) {
			newFilter.premiumOnly = Boolean(premiumOnly)
		}

		if (trip) {
			const tripIds = Array.isArray(trip) ? trip : [trip]
			newFilter.peaks = tripIds.map((id) => +id)
		}

		setFilter(newFilter) // Set the filter state only once
	}, []) // This effect is for initializing the filter from the URL

	// Handlers for filter changes
	const handleAgencyChange = (value: string) => {
		setUserTouchedFilter((prev) => ({ ...prev, agencyName: value }))
	}

	const handleCountriesChange = (countryCodes: string[]) => {
		setUserTouchedFilter((prev) => ({ ...prev, countries: countryCodes }))
	}

	const handlePeaksChange = (tripsIds: number[]) => {
		setUserTouchedFilter((prev) => ({ ...prev, peaks: tripsIds }))
	}

	const titleH1 = `${
		version === Types.VERSION.TREKKING
			? 'Trekking'
			: 'Mountaineering & Climbing'
	} Guide Directory`

	const seo = {
		title: `${titleH1} - ${
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks'
		}`,
		description:
			version === Types.VERSION.TREKKING
				? 'Browse a directory of trekking guides on Guided Trekking. Find experienced professionals offering guided treks and trips around the world.'
				: 'Browse a directory of mountaineering guides on Guided Peaks. Find experienced professionals offering guided climbs and expeditions around the world.',
		canonical: `https://www.guided${
			version === Types.VERSION.TREKKING ? 'trekking' : 'peaks'
		}.com/guides`,
		images: [
			{
				url: getDefaultImage(version),
				type: 'image/jpeg',
			},
		],
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
	}

	const filteredGuides = React.useMemo(() => {
		const nameFiltered =
			filter.agencyName === ''
				? guideOverviews
				: guideOverviews.filter((guide) =>
						guide.companyName
							.toLowerCase()
							.startsWith(filter.agencyName.toLowerCase()),
				  )

		const countryFiltered =
			filter.countries.length === 0
				? nameFiltered
				: nameFiltered.filter((guide) =>
						filter.countries.every((country) =>
							guide.countries.includes(country),
						),
				  )

		const peakFiltered =
			filter.peaks.length === 0
				? countryFiltered
				: countryFiltered.filter((guide) =>
						filter.peaks.every(
							(peak) =>
								guide.guideTripsSummary.some((trip) => trip.tripId === peak) ||
								guide.guideTripMultiPeaksSummary.some((trip) =>
									trip.individualTrips
										?.map((trip) => trip.tripId)
										.includes(peak),
								),
						),
				  )

		const certifiedFiltered = !filter.certifiedOnly
			? peakFiltered
			: peakFiltered.filter((guide) => guide.certificates.length > 0)

		const premiumFiltered = !filter.premiumOnly
			? certifiedFiltered
			: certifiedFiltered.filter((guide) => guide.isPremium)

		return premiumFiltered
	}, [
		filter.agencyName,
		filter.countries,
		filter.peaks,
		filter.certifiedOnly,
		filter.premiumOnly,
		guideOverviews,
	])

	const availableTrips = React.useMemo(() => {
		const allTrips = guideOverviews.flatMap((guide) =>
			(guide?.guideTripsSummary ?? []).map((trip) => ({
				id: (trip.tripId as number).toString(),
				name: trip.name as string,
				firstUploadId: (trip?.uploadId ?? -1).toString(),
			})),
		)

		// Use a Map to keep only unique trips based on their ID
		const uniqueTripsMap = new Map(allTrips.map((trip) => [trip.id, trip]))

		return Array.from(uniqueTripsMap.values())
	}, [guideOverviews])

	return (
		<>
			<Head>
				<title>{seo.title}</title>
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<div id="guides-page">
					<Paper
						shadow="md"
						radius="md"
						withBorder
						mb="xl"
						style={() => ({
							borderColor: guideOverviews.some((guide) => guide.isPremium)
								? ACCENT_COLOUR
								: '#e0e0e0',
							background: 'white',
							position: 'relative',
							overflow: 'hidden',
						})}
					>
						{guideOverviews.some((guide) => guide.isPremium) && (
							<div
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									width: '100%',
									height: '4px',
									background: ACCENT_COLOUR,
								}}
							/>
						)}

						<div style={{ padding: '25px 20px 10px' }}>
							<Title
								order={1}
								style={{
									fontSize: '2.5rem',
									fontWeight: 700,
									marginBottom: '10px',
								}}
							>
								{titleH1}
							</Title>

							<Text mb="md" size="lg">
								Compare{' '}
								<strong>
									{guideOverviews.length}{' '}
									{version === Types.VERSION.TREKKING
										? 'trekking'
										: 'mountaineering'}{' '}
									guides
								</strong>{' '}
								with expeditions in{' '}
								<strong>{distinctCountries.length} countries</strong>.
								{guideOverviews.some((guide) => guide.isPremium) && (
									<span style={{ marginLeft: '8px' }}>
										Guide companies with the{' '}
										<Badge
											variant="filled"
											style={{
												background: 'linear-gradient(135deg, #FFD700, #B8860B)',
												color: '#000',
												padding: '6px 12px',
												borderRadius: '20px',
												boxShadow: '0 2px 6px rgba(184, 134, 11, 0.4)',
												border: '1px solid rgba(255, 255, 255, 0.4)',
											}}
										>
											<span
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '4px',
													fontWeight: 'bold',
												}}
											>
												<span>Recommended</span> <span>★</span>
											</span>
										</Badge>{' '}
										badge have been vetted for quality.
									</span>
								)}
							</Text>
						</div>

						<div style={{ padding: '0 20px 20px' }}>
							<Grid align="center">
								<Grid.Col span={{ base: 12, sm: 5, md: 3 }}>
									<TextInput
										placeholder="Agency name"
										value={filter.agencyName}
										onChange={(e) => handleAgencyChange(e.currentTarget.value)}
										size="md"
									/>
								</Grid.Col>
								<Grid.Col span={{ base: 12, sm: 7, md: 3 }}>
									<CountryFilterInput
										countryCodes={distinctCountries}
										onChange={handleCountriesChange}
										placeholder="Countries"
										initialValue={filter.countries}
									/>
								</Grid.Col>
								<Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
									<TripFilterInput
										trips={availableTrips}
										onChange={handlePeaksChange}
										placeholder="Mountains"
										initialValue={filter.peaks}
									/>
								</Grid.Col>
								<Grid.Col
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '10px',
									}}
									span={{ base: 12, sm: 6, md: 3 }}
								>
									<div
										style={{
											display: 'flex',
											gap: '8px',
											alignItems: 'center',
										}}
									>
										<Checkbox
											checked={filter.certifiedOnly}
											label="Certified guides only"
											size="md"
											onChange={(e) => {
												setUserTouchedFilter({
													...filter,
													certifiedOnly: e.target.checked,
												})
											}}
										/>
									</div>
									<div
										style={{
											display: 'flex',
											gap: '8px',
											alignItems: 'center',
										}}
									>
										<Checkbox
											checked={filter.premiumOnly}
											label={
												<span
													style={{
														display: 'inline-flex',
														alignItems: 'center',
													}}
												>
													<Badge
														variant="filled"
														style={{
															background:
																'linear-gradient(135deg, #FFD700, #B8860B)',
															color: '#000',
															padding: '6px 12px',
															borderRadius: '20px',
															boxShadow: '0 2px 6px rgba(184, 134, 11, 0.4)',
															border: '1px solid rgba(255, 255, 255, 0.4)',
														}}
													>
														<span
															style={{
																display: 'flex',
																alignItems: 'center',
																gap: '4px',
																fontWeight: 'bold',
															}}
														>
															<span>Recommended</span> <span>★</span>
														</span>
													</Badge>
													<span style={{ marginLeft: 10, fontWeight: 500 }}>
														only
													</span>
												</span>
											}
											size="md"
											styles={{
												input: {
													'&:checked': {
														backgroundColor: ACCENT_COLOUR,
														borderColor: ACCENT_COLOUR,
													},
												},
											}}
											onChange={(e) => {
												setUserTouchedFilter({
													...filter,
													premiumOnly: e.target.checked,
												})
											}}
										/>
									</div>
								</Grid.Col>
							</Grid>
							{(!!filter.agencyName ||
								filter.countries.length > 0 ||
								filter.peaks.length > 0 ||
								filter.certifiedOnly ||
								filter.premiumOnly) &&
								filteredGuides.length === 0 && (
									<Text mt="md" fw={500}>
										0 matching guides
									</Text>
								)}
						</div>
					</Paper>
					<GuideDirectoryList
						guides={filteredGuides}
						highlightedCountryISOCodes={filter.countries}
						highlightedTripIds={filter.peaks}
					/>
				</div>
			</PageTemplate>
		</>
	)
}

export const getServerSideProps = withVersion(
	async (context: { version: Types.VERSION }) => {
		if (context.version === Types.VERSION.TREKKING) {
			return { notFound: true }
		}

		const guideOverviews: Types.API.GuideCompanyOverview[] = await RedisUtil.Data.GuideDirectory.get(
			{ version: context.version },
		)

		// Filter out the premiumExpiresAt field to prevent serialization issues
		const serializedGuideOverviews = guideOverviews.map((guide) => {
			const { premiumExpiresAt, ...rest } = guide
			return rest
		})

		const distinctCountries = guideOverviews
			.flatMap((guide) => guide.countries)
			.filter((country, index, self) => self.indexOf(country) === index)
			.sort()

		return {
			props: {
				guideOverviews: serializedGuideOverviews,
				distinctCountries,
				version: context?.version,
			},
		}
	},
)

export default GuidesPage
